/**
 * 설정 > 파트너 관리.
 * 파트너사를 골라 파트너스테이션 로그인 계정을 조회·생성·수정한다.
 * 삭제 API가 없어 계정을 막을 때는 수정 모달의 「사용 여부」를 끈다.
 */
import { KeyRound, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import type { PartnerCompanyAccount } from '@/entities/partner-company/api/partner-company.api';
import {
  useCreatePartnerCompanyAccount,
  usePartnerCompanyAccounts,
  usePartnerCompanyOptions,
  useUpdatePartnerCompanyAccount,
} from '@/entities/partner-company/hooks/usePartnerCompanies';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Field } from '@/shared/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { formatDateTime } from '@/shared/lib/dateFormat';
import { handleApiError, useToast } from '@/shared/utils/toast';

import {
  buildPartnerAccountUpdateRequest,
  PartnerAccountFormModal,
  type PartnerAccountFormValues,
} from './PartnerAccountFormModal';

/** 모달 상태 — null이면 닫힘, account가 null이면 생성 모드 */
type ModalState = { account: PartnerCompanyAccount | null } | null;

export const SettingsPartnersPage: React.FC = () => {
  const toast = useToast();
  const [partnerCompanyId, setPartnerCompanyId] = useState<number | null>(null);
  const [modal, setModal] = useState<ModalState>(null);

  const { data: options = [], isLoading: optionsLoading } =
    usePartnerCompanyOptions();
  const {
    data: accounts = [],
    isLoading: accountsLoading,
    isError,
    error,
  } = usePartnerCompanyAccounts(partnerCompanyId);
  const createMutation = useCreatePartnerCompanyAccount();
  const updateMutation = useUpdatePartnerCompanyAccount();

  useEffect(() => {
    if (isError && error) handleApiError(error, toast);
  }, [isError, error, toast]);

  const companyName =
    options.find((option) => option.partnerCompanyId === partnerCompanyId)
      ?.companyName ?? '';

  const handleSubmit = async (values: PartnerAccountFormValues) => {
    if (partnerCompanyId == null || !modal) return;
    try {
      if (modal.account) {
        const data = buildPartnerAccountUpdateRequest(modal.account, values);
        if (Object.keys(data).length === 0) {
          toast.info('변경된 내용이 없습니다.');
          return;
        }
        await updateMutation.mutateAsync({
          partnerCompanyId,
          accountId: modal.account.accountId,
          data,
        });
        toast.success('계정 정보가 저장되었습니다.');
      } else {
        await createMutation.mutateAsync({
          partnerCompanyId,
          data: {
            loginEmail: values.loginEmail.trim(),
            name: values.name.trim(),
            password: values.password,
          },
        });
        toast.success(`${values.loginEmail.trim()} 계정이 생성되었습니다.`);
      }
      setModal(null);
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const renderTable = () => {
    if (partnerCompanyId == null) {
      return <EmptyState description="계정을 조회할 파트너사를 선택하세요." />;
    }
    if (accountsLoading) {
      return <LoadingSpinner tip="계정 목록을 불러오는 중..." />;
    }
    if (accounts.length === 0) {
      return (
        <EmptyState description="등록된 파트너스테이션 계정이 없습니다." />
      );
    }
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>로그인 이메일</TableHead>
            <TableHead>담당자명</TableHead>
            <TableHead>사용 여부</TableHead>
            <TableHead>생성일시</TableHead>
            <TableHead>수정일시</TableHead>
            <TableHead className="w-20">관리</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {accounts.map((account) => (
            <TableRow key={account.accountId}>
              <TableCell className="font-medium text-foreground">
                {account.loginEmail}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {account.name}
              </TableCell>
              <TableCell>
                <Badge variant={account.active ? 'success' : 'secondary'}>
                  {account.active ? '사용' : '미사용'}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatDateTime(account.createdAt)}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatDateTime(account.updatedAt)}
              </TableCell>
              <TableCell>
                <MenuWriteGate>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto px-0"
                    onClick={() => setModal({ account })}
                  >
                    수정
                  </Button>
                </MenuWriteGate>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={KeyRound}
        title="파트너 관리"
        description="파트너스테이션 로그인 계정 조회 및 생성·수정"
        actions={
          <MenuWriteGate>
            <Button
              className="ruda-button"
              disabled={partnerCompanyId == null}
              onClick={() => setModal({ account: null })}
            >
              <Plus className="mr-2 h-4 w-4" />
              계정 생성
            </Button>
          </MenuWriteGate>
        }
      />

      <FilterCard>
        <Field
          label="파트너사"
          htmlFor="settings-partner-company"
          className="w-full sm:w-[320px]"
        >
          <Select
            value={partnerCompanyId != null ? String(partnerCompanyId) : ''}
            onValueChange={(value) => setPartnerCompanyId(Number(value))}
            disabled={optionsLoading}
          >
            <SelectTrigger id="settings-partner-company">
              <SelectValue placeholder="파트너사 선택" />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem
                  key={option.partnerCompanyId}
                  value={String(option.partnerCompanyId)}
                >
                  {option.companyName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </FilterCard>

      <DataCard>{renderTable()}</DataCard>

      <PartnerAccountFormModal
        open={modal != null}
        companyName={companyName}
        account={modal?.account ?? null}
        submitting={createMutation.isPending || updateMutation.isPending}
        onClose={() => setModal(null)}
        onSubmit={(values) => void handleSubmit(values)}
      />
    </div>
  );
};
